import React, { useEffect, useMemo, useState, useRef } from "react";
import { Attention, Grid, GridCell, Typography } from "@optimizely/axiom";
import { UserGroup } from "../../../domain/UserGroup";
import { GroupClassifier } from "../../../lib/group-classifier";
import { IOrganizationProductInstance } from "../../../domain/OrganizationProduct";
import { InstanceDropdown } from "../InstanceDropdown/InstanceDropdown";
import { RoleSelector } from "../RoleSelector/RoleSelector";
import { useRoles } from "../../../hooks/useRoles/useRoles";
import { ALL_PAGES_SIZE } from "../../../constants/constants";
import { useUserContext } from "../../../providers/UserProvider";
import { Role } from "../../../domain/Role";
import { AccessDetailsByInstanceId } from "../../../domain/AccessDetails";
import { useOpalBranding } from "../../../hooks/useOpalBranding/useOpalBranding";

type OpalRoleSuggestionSelectionProps = {
    availableGroups?: UserGroup[];
    className?: string;
    currentGroup?: UserGroup | null;
    disabled?: boolean;
    existingAccess?: AccessDetailsByInstanceId | null;
    onChange: ({
        value,
        dismissed,
        instance,
        roleId
    }: {
        dismissed?: boolean;
        value: UserGroup | null;
        instance?: IOrganizationProductInstance | null;
        roleId?: string | null;
    }) => void;
    opalInstances?: IOrganizationProductInstance[];
    skip?: boolean;
    value?: UserGroup | null; // selectedGroup
};

export const OpalRoleSuggestionSelection = ({
    currentGroup = null,
    availableGroups = [],
    className,
    disabled = false,
    existingAccess = null,
    onChange,
    opalInstances = [],
    skip = false,
    value
}: OpalRoleSuggestionSelectionProps) => {
    const { organizationId } = useUserContext();
    const { opalName } = useOpalBranding();

    const [selectedInstance, setSelectedInstance] = useState<IOrganizationProductInstance | null>(null);

    // Determine canonical user/admin groups (global)
    const { hasValidGroups } = useMemo(() => {
        return GroupClassifier.classifyGroups(availableGroups);
    }, [availableGroups]);

    // Instance-aware classification (only groups that actually have roles for selected instance)
    const { userGroup: instanceUserGroup, adminGroup: instanceAdminGroup } = useMemo(
        () => GroupClassifier.classifyGroupsForInstance(availableGroups, selectedInstance?.id),
        [availableGroups, selectedInstance?.id]
    );

    // Load roles for the selected Opal instance (if any)
    const { roles: opalRoles, isLoading: opalRolesLoading } = useRoles({
        organizationId,
        productId: process.env.REACT_APP_OPAL_PRODUCT_ID,
        instanceId: selectedInstance?.id,
        size: ALL_PAGES_SIZE,
        skip: !selectedInstance?.id || !organizationId
    });

    // Selected role derived from currently selected group value
    const selectedRole = useMemo(() => {
        if (!value || !selectedInstance?.id || !opalRoles?.length) return undefined;
        const roleIds = GroupClassifier.getRoleIdsForGroupInstance(value, selectedInstance.id);
        return opalRoles.find((r) => roleIds.includes(r.id)) || undefined;
    }, [value, opalRoles, selectedInstance]);

    const lastPropagatedRef = useRef<{ groupId: string | null; instanceId: string | null; roleId: string | null }>({
        groupId: null,
        instanceId: null,
        roleId: null
    });

    // If instance list has exactly one entry, auto-select it (but allow manual change afterwards)
    useEffect(() => {
        if (!selectedInstance && opalInstances.length === 1) {
            const onlyInstance = opalInstances[0];
            setSelectedInstance(onlyInstance);
            // Propagate upward if a group is already selected
            if (value) {
                const roleIds = GroupClassifier.getRoleIdsForGroupInstance(value, onlyInstance.id);
                const role = opalRoles?.find((r) => roleIds.includes(r.id)) || null;
                onChange({ value, instance: onlyInstance, roleId: role ? role.id : null });
                lastPropagatedRef.current = {
                    groupId: value.id,
                    instanceId: onlyInstance.id,
                    roleId: role ? role.id : null
                };
            }
        }
    }, [opalInstances, selectedInstance, value, opalRoles, onChange]);

    // After roles load (opalRoles populated) ensure roleId gets propagated if it was missing during auto-select
    useEffect(() => {
        if (value && selectedInstance?.id && selectedRole?.id) {
            const { groupId, instanceId, roleId } = lastPropagatedRef.current;
            if (groupId !== value.id || instanceId !== selectedInstance.id || roleId !== selectedRole.id) {
                onChange({ value, instance: selectedInstance, roleId: selectedRole.id });
                lastPropagatedRef.current = {
                    groupId: value.id,
                    instanceId: selectedInstance.id,
                    roleId: selectedRole.id
                };
            }
        }
    }, [value, selectedInstance, selectedRole, onChange]);

    const existingAccessForSelectedOpalInstance = useMemo(() => {
        if (!selectedInstance?.id || !existingAccess) return null;
        return existingAccess[selectedInstance.id]?.flat() || null;
    }, [selectedInstance?.id, existingAccess]);
    const alreadySelectedUserGroup = existingAccessForSelectedOpalInstance?.find((i) =>
        GroupClassifier.isUser({ name: i.groupName })
    )?.roles[0];

    // Check if user has ANY Opal access across any instance
    const hasAnyOpalAccess = useMemo(() => {
        return (
            opalInstances?.some((i) => {
                const rows = existingAccess?.[i.id];
                return rows && rows.length > 0;
            }) ?? false
        );
    }, [opalInstances, existingAccess]);

    // validate that org has available groups otherwise don't render
    if (!hasValidGroups) {
        return null;
    }

    // If user has any Opal access, don't show suggestions
    if (hasAnyOpalAccess) {
        return null;
    }

    // Handle selection of simplified role
    const handleRoleChanged = (role: Role) => {
        if (!role || !selectedInstance?.id) {
            onChange({ value: null, instance: selectedInstance, roleId: null });
            return;
        }
        const group = GroupClassifier.findGroupForRole({
            groups: availableGroups,
            roleId: role.id,
            instanceId: selectedInstance.id
        });
        onChange({ value: group, instance: selectedInstance, roleId: role.id });
    };

    const existingOpalRoleIds =
        existingAccessForSelectedOpalInstance?.flatMap((row) => row.roles?.map((r) => r.id) || []) || [];
    const opalUserGroupRoleIds = instanceUserGroup
        ? GroupClassifier.getRoleIdsForGroupInstance(instanceUserGroup, selectedInstance?.id)
        : [];
    const opalAdminGroupRoleIds = instanceAdminGroup
        ? GroupClassifier.getRoleIdsForGroupInstance(instanceAdminGroup, selectedInstance?.id)
        : [];
    const hasUserRoleForInstance =
        existingOpalRoleIds?.length > 0 && opalUserGroupRoleIds?.every((rid) => existingOpalRoleIds?.includes(rid));
    const hasAdminRoleForInstance =
        existingOpalRoleIds?.length > 0 && opalAdminGroupRoleIds?.every((rid) => existingOpalRoleIds?.includes(rid));

    const handleDismiss = () => {
        onChange({ value: null, instance: selectedInstance, roleId: null, dismissed: true });
    };

    return (
        <Attention
            type="warning"
            alignment="left"
            className={className}
            onDismiss={handleDismiss}
            data-cy="opal-role-suggestion"
        >
            <Typography type="subhead" tag="div" className="push--bottom">
                {!!hasUserRoleForInstance
                    ? `Upgrade this user's ${opalName} access:`
                    : `Include ${opalName} access for this user:`}
            </Typography>
            <Grid className="push-double--bottom">
                <GridCell small={2} medium={4} large={6} xlarge={6}>
                    {opalInstances && (
                        <InstanceDropdown
                            value={!skip ? selectedInstance?.id : undefined}
                            disabled={skip || disabled}
                            white
                            fullWidth
                            instances={opalInstances}
                            onChange={({ value: inst }) => {
                                const instance = inst as IOrganizationProductInstance;
                                setSelectedInstance(instance);
                                // Propagate upward if a group is already selected
                                if (value) {
                                    const roleIds = GroupClassifier.getRoleIdsForGroupInstance(value, instance.id);
                                    const role = opalRoles?.find((r) => roleIds.includes(r.id)) || null;
                                    onChange({ value, instance, roleId: role ? role.id : null });
                                    lastPropagatedRef.current = {
                                        groupId: value.id,
                                        instanceId: instance.id,
                                        roleId: role ? role.id : null
                                    };
                                }
                            }}
                        />
                    )}
                </GridCell>
                <GridCell small={2} medium={4} large={6} xlarge={6}>
                    {hasAdminRoleForInstance ? (
                        <Typography type="body">
                            You've already selected the Admin role for this instance. If you wish to modify your
                            selection remove it below.
                        </Typography>
                    ) : (
                        <RoleSelector
                            loading={Boolean(selectedInstance && opalRolesLoading)}
                            onRoleChanged={handleRoleChanged}
                            roles={opalRoles}
                            preExistingRoleIds={existingOpalRoleIds}
                            selectedRole={selectedRole || (alreadySelectedUserGroup as Role)}
                            isDisabled={skip || disabled || !selectedInstance?.id || !opalRoles?.length}
                        />
                    )}
                </GridCell>
            </Grid>
        </Attention>
    );
};

OpalRoleSuggestionSelection.displayName = "OpalRoleSuggestionSelection";
